import React from 'react';
import {
  Filter as FilterIcon,
  Table2,
  CandlestickChart,
  BellRing,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STEPS: Array<{
  number: string;
  iconKey: 'step1' | 'step2' | 'step3' | 'step4';
  Icon: React.ElementType;
}> = [
  { number: '01', iconKey: 'step1', Icon: FilterIcon },
  { number: '02', iconKey: 'step2', Icon: Table2 },
  { number: '03', iconKey: 'step3', Icon: CandlestickChart },
  { number: '04', iconKey: 'step4', Icon: BellRing },
];

/**
 * WorkflowSection - single canonical "From rule to alert in four steps"
 * panel. Replaces the duplicate hero glass-card AND standalone "How It
 * Works" section that used to live in LandingPage.tsx.
 *
 * TODO(landing-screenshots): swap the icon tile for a small inline UI
 * screenshot per step (filter modal / results table / chart with MAs /
 * alert email) once production captures land per K12.
 */
const WorkflowSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section id="how" className="py-4 py-md-5" aria-labelledby="how-headline">
      <div className="container">
        <div className="text-center mb-4">
          <p
            className="landing-eyebrow mb-2"
            style={{
              color: 'var(--accent-primary)',
            }}
          >
            {t('landing.workflow.eyebrow')}
          </p>
          <h2
            id="how-headline"
            className="landing-section__h2 fw-semibold mb-2"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--text-primary)',
            }}
          >
            {t('landing.workflow.headline')}
          </h2>
          <p
            className="landing-section__sub mb-0 mx-auto"
            style={{ color: 'var(--text-secondary)', maxWidth: 640 }}
          >
            {t('landing.workflow.sub')}
          </p>
        </div>

        <div className="row g-3" role="list">
          {STEPS.map((step, index) => {
            const Icon = step.Icon;
            return (
              <div
                key={step.number}
                role="listitem"
                className="col-12 col-md-6 col-lg-3"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 120}ms forwards`,
                  opacity: '0',
                }}
              >
                <article
                  className="glass-card h-100 p-4 position-relative"
                  data-testid={`workflow-step-${step.number}`}
                  style={{
                    borderLeft: '3px solid var(--accent-primary)',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <span
                      className="fw-semibold"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--accent-primary)',
                        opacity: 0.55,
                        fontSize: '1.5rem',
                        lineHeight: 1,
                      }}
                      aria-hidden
                    >
                      {step.number}
                    </span>
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2"
                      style={{
                        width: 36,
                        height: 36,
                        backgroundColor: 'var(--accent-primary-muted)',
                        border: '1px solid rgba(242, 202, 80, 0.35)',
                        color: 'var(--accent-primary)',
                      }}
                      aria-hidden
                    >
                      <Icon size={18} strokeWidth={2} />
                    </div>
                  </div>

                  <h3
                    className="landing-card__title fw-semibold mb-2"
                    style={{
                      fontFamily: 'var(--font-display)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t(`landing.workflow.${step.iconKey}.title`)}
                  </h3>

                  <p
                    className="landing-card__body mb-0"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {t(`landing.workflow.${step.iconKey}.body`)}
                  </p>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;
